import { ReactNode, useEffect, useState } from "react";
import { DocFieldConfig } from "../../models/config/DocFieldConfig";
import { DefaultValueMatcher } from "../../models/matchers/DefaultValueMatcher";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { FuzzyFieldMatcher } from "../../models/matchers/FuzzyFieldMatcher";
import { KeyValueMatcher } from "../../models/matchers/KeyValueMatcher";
import { RegexFieldMatcher } from "../../models/matchers/RegexFieldMatcher";
import { ExpandingTooltip } from "../tooltips/ExpandingTooltip";
import { DefaultValueDlfEditor } from "./defaultValue/DefaultValueDlfEditor";
import { FuzzyFieldMatcherEditor } from "./fuzzy/FuzzyFieldMatcherEditor";
import { KeyValueDlfEditor } from "./keyValue/KeyValueDlfEditor";
import { RegexFieldMatcherEditor } from "./regex/RegexFieldMatcherEditor";

export {
  FieldMatcherEditor,
  Props as FieldMatcherEditorProps,
  FieldMatcherEditorChildProps,
};

/**
 * Define props child components may received from the FieldMatcherEditor component.
 * TODO: updatee jdoc
 */
type Props<T extends DlfMatcher> = {
  matcher: T;
  docFieldConfig: DocFieldConfig;
  update(matcher: T, oldMatcherId: string): void;
  /**
   * If defined, the component allows for the DlfMatcherType to be changed.
   */
  changedMatcherType?: (matcher: DlfMatcher, oldMatcherId: string) => void;
  /**
   * Add a button component to the FieldMatcherEditor.
   */
  button?: ReactNode;
  /**
   * Text to display in FieldMatcherEditor label
   */
  label?: string;
  /**
   * Text color for label in FieldMatcherEditor
   */
  labelColor?: string;
  /**
   * Tooltip for label in FieldMatherEditor
   */
  labelTooltip?: ExpandingTooltip;
  /**
   * Background color for label in FieldMatcherEditor
   */
  labelBgColor?: string;
  /**
   * onClick handler for FieldMatcherEditor label
   */
  onClickLabel?(): void;
  index?: number;
};

type FieldMatcherEditorChildProps<T extends DlfMatcher> = Omit<
  Props<T>,
  "update" | "changedMatcherType"
> & {
  update(matcher: DlfMatcher): void;
  changedMatcherType?: (matcher: DlfMatcher) => void;
};

const FieldMatcherEditor = (props: Props<DlfMatcher>) => {
  const [oldMatcherId, setOldMatcherId] = useState(
    props.matcher.getMatcherId(),
  );

  // update oldMatcherId when matcher prop changes
  useEffect(
    () => setOldMatcherId(props.matcher.getMatcherId()),
    [props.matcher],
  );

  const update = (matcher: DlfMatcher) => props.update(matcher, oldMatcherId);

  const changedMatcherType = props.changedMatcherType
    ? (matcher: DlfMatcher) => props.changedMatcherType!(matcher, oldMatcherId)
    : undefined;

  switch (props.matcher.getDiscriminator()) {
    case "F_DEFAULT": {
      return (
        <DefaultValueDlfEditor
          key={props.matcher.getMatcherId()}
          {...props}
          update={update}
          changedMatcherType={changedMatcherType}
          matcher={props.matcher as DefaultValueMatcher}
        />
      );
    }
    case "F_REGEX": {
      return (
        <RegexFieldMatcherEditor
          key={props.matcher.getMatcherId()}
          {...props}
          matcher={props.matcher as RegexFieldMatcher}
          changedMatcherType={changedMatcherType}
          update={update}
        />
      );
    }
    case "F_KEYVALUE": {
      return (
        <KeyValueDlfEditor
          key={props.matcher.getMatcherId()}
          {...props}
          matcher={props.matcher as KeyValueMatcher}
          changedMatcherType={changedMatcherType}
          update={update}
        />
      );
    }
    case "F_FUZZY": {
      return (
        <FuzzyFieldMatcherEditor
          key={props.matcher.getMatcherId()}
          {...props}
          matcher={props.matcher as FuzzyFieldMatcher}
          update={update}
          changedMatcherType={changedMatcherType}
        />
      );
    }
    default: {
      return null;
    }
  }
};
